import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { alpha } from '@mui/material/styles';

const KPI_DEFINITIONS = [
  { key: 'received', label: 'Orders Received', icon: Inventory2OutlinedIcon, color: '#1565c0' },
  { key: 'dispatched', label: 'Orders Dispatched', icon: CheckCircleOutlineIcon, color: '#16856a' },
  { key: 'cancelled', label: 'Cancelled', icon: CancelOutlinedIcon, color: '#c62828', inverseTrend: true },
  { key: 'remaining', label: 'Remaining', icon: ScheduleOutlinedIcon, color: '#8a5a00', inverseTrend: true },
  { key: 'dispatchRate', label: 'Dispatch Rate', icon: PercentOutlinedIcon, color: '#6a3db4', isPercent: true },
];

const formatValue = (value, isPercent) => (
  isPercent
    ? `${Number(value || 0).toFixed(1)}%`
    : Number(value || 0).toLocaleString()
);

const ReportKpiCards = ({ current, previous, showComparison }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, minmax(0, 1fr))',
        lg: 'repeat(5, minmax(0, 1fr))',
      },
      gap: 0.8,
    }}
  >
    {KPI_DEFINITIONS.map((definition) => {
      const currentValue = Number(current?.[definition.key] || 0);
      const previousValue = Number(previous?.[definition.key] || 0);
      const delta = currentValue - previousValue;
      const improved = definition.inverseTrend ? delta < 0 : delta > 0;
      const worsened = definition.inverseTrend ? delta > 0 : delta < 0;
      const TrendIcon = delta > 0 ? TrendingUpIcon : delta < 0 ? TrendingDownIcon : TrendingFlatIcon;
      const Icon = definition.icon;

      return (
        <Paper
          key={definition.key}
          variant="outlined"
          sx={{
            px: 0.9,
            py: 0.45,
            minWidth: 0,
            borderRadius: 2,
            borderColor: (theme) => alpha(definition.color, theme.palette.mode === 'dark' ? 0.45 : 0.18),
            background: (theme) => `linear-gradient(145deg, ${alpha(definition.color, 0.08)}, ${theme.palette.background.paper} 62%)`,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, fontWeight: 800, lineHeight: 1.1 }}>
                {definition.label}
              </Typography>
              <Typography sx={{ mt: 0.05, fontSize: 17, fontWeight: 900, lineHeight: 1.05, color: 'text.primary' }}>
                {formatValue(currentValue, definition.isPercent)}
              </Typography>
            </Box>
            <Box sx={{ p: 0.5, borderRadius: 1.5, bgcolor: alpha(definition.color, 0.12), color: definition.color }}>
              <Icon sx={{ fontSize: 17 }} />
            </Box>
          </Stack>
          {showComparison && (
            <Stack direction="row" spacing={0.3} alignItems="center" sx={{ mt: 0.15 }}>
              <TrendIcon
                sx={{
                  fontSize: 12,
                  color: improved ? 'success.main' : worsened ? 'error.main' : 'text.secondary',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  fontSize: 9.5,
                  color: improved ? 'success.main' : worsened ? 'error.main' : 'text.secondary',
                }}
              >
                {delta > 0 ? '+' : ''}{definition.isPercent ? delta.toFixed(1) : delta.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9.5 }}>
                vs previous
              </Typography>
            </Stack>
          )}
        </Paper>
      );
    })}
  </Box>
);

export default ReportKpiCards;
